import React from 'react';
import { useTranslation } from 'react-i18next';

import { useLoginMutation } from '@/api/hooks/useAuth';
import { LoginResponse } from '@/api/lib/auth';
import { apiUrl } from '@/configs/config';

import { Typography } from '@/components/atoms/Typography';
import { TextInput } from '@/components/forms/base/TextInput';
import { Form } from '@/components/forms/core/Form';
import { Button } from '@/components/molecules/Button';

import { LoginSchema, LoginSchemaType } from './schema';
import { FaApple, FaGoogle } from 'react-icons/fa';

export interface LoginFormProps {
  onSuccess?: (data: LoginResponse) => void;
}

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const { t } = useTranslation();

  const loginMutation = useLoginMutation({
    onSuccess
  });

  const handleSubmit = (data: LoginSchemaType) => {
    loginMutation.mutate(data);
  };

  return (
    <div className="w-full">
      <div className="flex space-y-3 flex-col xl:text-left text-center xl:items-start items-center">
        <Typography variant="h1" weight="bold">
          {t('authentication.login')}
        </Typography>
        <Typography variant="h4">{t('authentication.loginAccountWithEmailTitle')}</Typography>
      </div>

      <div className="py-4 text-base w-full leading-6  text-gray-700 sm:text-lg sm:leading-7">
        <Form
          data-testid="login-form"
          schema={LoginSchema}
          onSubmit={handleSubmit}
          className="flex flex-col gap-2 w-full"
          render={({ formState }) => (
            <>
              <TextInput id="email" placeholder={t('authentication.loginEmailPlaceholder')} />
              <Button
                type="submit"
                color="primary"
                label={t('authentication.loginWithEmail')}
                loading={loginMutation.isLoading}
                fullWidth
                disabled={!formState.isDirty}
              />
            </>
          )}
        />

        {/* Separator with "OR" text */}
        <div className="flex items-center py-5 xl:py-7">
          <div className="flex-grow border-t border-gray-300" />
          <Typography color="gray" marginClassName="mx-4">
            {t('common.or', 'OU')}
          </Typography>
          <div className="flex-grow border-t border-gray-300" />
        </div>

        <span className="space-y-3">
          {/* Google login button */}
          <Button
            type="button"
            color="black"
            label={t('authentication.loginWithGoogle', 'Login with Google')}
            href={`${apiUrl}/protected/auth/google`}
            fullWidth
            icon={<FaGoogle size={16} />}
          />

          {/* Apple login button */}
          <Button
            type="button"
            color="black"
            label={t('authentication.loginWithApple', 'Login with Apple')}
            href={`${apiUrl}/protected/auth/apple`}
            fullWidth
            icon={<FaApple size={20} />}
          />
        </span>
      </div>
    </div>
  );
};
